/**
 * Stadium & Economy API Service (Microservicio de Estadio y Economía)
 * Conecta con los endpoints del backend y en caso de que aún no existan o fallen,
 * simula la llamada cargando el mock (.json) desde /mocks/stadium-economy/
 */
import { apiSimulator } from './api-simulator.service';

export const stadiumEconomyApi = {
  /**
   * PUT /api/v1/leagues/:leagueId/stadium/tickets
   * Mock fallback: /mocks/stadium-economy/update-tickets.json
   */
  async updateTicketPricing(
    leagueId: string,
    ticketPrice: number,
    seasonTicketPrice?: number
  ): Promise<{
    success: boolean;
    ticketPrice: number;
    seasonTicketPrice?: number;
  }> {
    return apiSimulator.callWithMockFallback({
      method: 'PUT',
      endpoint: `/api/v1/leagues/${encodeURIComponent(leagueId)}/stadium/tickets`,
      body: { ticketPrice, seasonTicketPrice },
      service: 'stadium-economy',
      mockName: 'update-tickets',
      transformMock: (mock) => ({
        ...mock,
        ticketPrice,
        seasonTicketPrice: seasonTicketPrice ?? mock.seasonTicketPrice,
      }),
    });
  },

  /**
   * POST /api/v1/leagues/:leagueId/stadium/upgrade-capacity
   * Mock fallback: /mocks/stadium-economy/upgrade-capacity.json
   */
  async upgradeCapacity(
    leagueId: string,
    newCapacity: number,
    costM: number
  ): Promise<{ success: boolean; newCapacity: number; costM: number }> {
    return apiSimulator.callWithMockFallback({
      method: 'POST',
      endpoint: `/api/v1/leagues/${encodeURIComponent(leagueId)}/stadium/upgrade-capacity`,
      body: { newCapacity, costM },
      service: 'stadium-economy',
      mockName: 'upgrade-capacity',
      transformMock: (mock) => ({
        ...mock,
        newCapacity,
        costM,
      }),
    });
  },

  /**
   * POST /api/v1/leagues/:leagueId/stadium/upgrade-module
   * Mock fallback: /mocks/stadium-economy/upgrade-module.json
   */
  async upgradeModule(
    leagueId: string,
    moduleType: 'parking' | 'cafeteria' | 'merchandising',
    targetLevel: number,
    costM: number
  ): Promise<{ success: boolean; moduleType: string; targetLevel: number }> {
    return apiSimulator.callWithMockFallback({
      method: 'POST',
      endpoint: `/api/v1/leagues/${encodeURIComponent(leagueId)}/stadium/upgrade-module`,
      body: { moduleType, targetLevel, costM },
      service: 'stadium-economy',
      mockName: 'upgrade-module',
      transformMock: (mock) => ({
        ...mock,
        moduleType,
        targetLevel,
      }),
    });
  },

  /**
   * POST /api/v1/leagues/:leagueId/economy/loans
   * Mock fallback: /mocks/stadium-economy/request-loan.json
   */
  async requestLoan(
    leagueId: string,
    amountM: number,
    termSeasons: number
  ): Promise<{ success: boolean; loanId: string; amountM: number }> {
    return apiSimulator.callWithMockFallback({
      method: 'POST',
      endpoint: `/api/v1/leagues/${encodeURIComponent(leagueId)}/economy/loans`,
      body: { amountM, termSeasons },
      service: 'stadium-economy',
      mockName: 'request-loan',
      transformMock: (mock) => ({
        ...mock,
        amountM,
        loanId: `loan-${Date.now()}`,
      }),
    });
  },

  /**
   * POST /api/v1/leagues/:leagueId/economy/subscription
   * Mock fallback: /mocks/stadium-economy/pay-subscription.json
   */
  async paySubscription(
    leagueId: string,
    monthlyFeeCr: number
  ): Promise<{ success: boolean; paidCr: number }> {
    return apiSimulator.callWithMockFallback({
      method: 'POST',
      endpoint: `/api/v1/leagues/${encodeURIComponent(leagueId)}/economy/subscription`,
      body: { monthlyFeeCr },
      service: 'stadium-economy',
      mockName: 'pay-subscription',
      transformMock: (mock) => ({
        ...mock,
        paidCr: monthlyFeeCr,
      }),
    });
  },
};
